import { useParams } from "@tanstack/react-router";
import { eventsDetailRoute } from "app/router";
import { Spinner } from "components/elements";
import { trpc } from "lib/trpc";
import { CalendarFriendlyEvent } from "./types";
import { EventDetailsBody } from "./EventDetailsBody";

/**
 * Routed event detail page at /events/$id. Renders the same body markup the
 * EventDetailsModal uses, just inline in a Card. The Zoom Meeting SDK
 * launcher lives at /events/$id/join (see EventJoin.tsx) — the body links
 * there via the Join Meeting button when applicable.
 */
export const EventDetailPage = () => {
    const eventId = +useParams({ from: eventsDetailRoute.id }).id;

    const { data: event, isLoading } = trpc.events.getById.useQuery({ id: eventId });

    if (isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!event) {
        return (
            <div className="mx-auto w-full pt-6">
                <div className="rounded-2xl bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-semibold">Event not found</h2>
                    <p className="mt-2 text-sm text-gray-600">
                        This event may have been removed or you may not have access.
                    </p>
                </div>
            </div>
        );
    }

    // tRPC delivers Date fields over the wire as ISO strings; coerce to Date
    // and fall back end → start when null (matching the list view).
    const start = new Date(event.start);
    const friendly: CalendarFriendlyEvent = {
        ...event,
        start,
        end: event.end ? new Date(event.end) : start,
    };

    return (
        <div className="mx-auto w-full pt-6">
            <div className="rounded-2xl bg-white p-6 shadow-sm">
                <EventDetailsBody event={friendly} />
            </div>
        </div>
    );
};

// Back-compat alias for any caller still importing `EventsView`.
export { EventDetailPage as EventsView };
